export const DATE_COLUMNS = new Set([
  'birth_date', 'birthDate', 'study_date', 'studyDate', 'studydate',
  'archiveDate', 'archivedate', 'observationDate', 'observation_date',
  'approval_time', 'report_approved_at'
]);

export const TEXT_OPERATORS = [
  'contains', 'notContains', 'equals', 'notEquals',
  'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty'
];

export const DATE_OPERATORS = [
  'is', 'before', 'after', 'isToday', 'isThisWeek', 'isThisMonth', 'isThisYear',
  'isEmpty', 'isNotEmpty'
];

export const OPERATORS_WITHOUT_VALUE = new Set([
  'isEmpty', 'isNotEmpty', 'isToday', 'isThisWeek', 'isThisMonth', 'isThisYear'
]);

export function isDateColumn(column) {
  return DATE_COLUMNS.has(column);
}

export function getOperatorsForColumn(column) {
  return isDateColumn(column) ? DATE_OPERATORS : TEXT_OPERATORS;
}

export function operatorNeedsValue(operator) {
  return !OPERATORS_WITHOUT_VALUE.has(operator);
}

export function createEmptyCondition() {
  return {
    id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    column: '',
    operator: 'contains',
    value: ''
  };
}

export function isConditionValid(condition) {
  if (!condition || !condition.column || !condition.operator) return false;
  if (!operatorNeedsValue(condition.operator)) return true;
  return condition.value != null && String(condition.value).trim() !== '';
}

export function getValidConditions(conditions) {
  return (Array.isArray(conditions) ? conditions : []).filter(isConditionValid);
}

function getFieldValue(row, field) {
  if (!row || !field) return undefined;
  if (row[field] != null && row[field] !== '') return row[field];
  const camel = field.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  const snake = field.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  if (row[camel] != null && row[camel] !== '') return row[camel];
  if (row[snake] != null && row[snake] !== '') return row[snake];
  return row[field] ?? row[camel] ?? row[snake];
}

function toSearchString(value) {
  if (value == null) return '';
  return String(value).toLowerCase();
}

function isEmptyValue(value) {
  return value == null || String(value).trim() === '';
}

export function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }

  const dotted = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})/);
  if (dotted) {
    let year = Number(dotted[3]);
    if (year < 100) {
      year += year >= 50 ? 1900 : 2000;
    }
    return new Date(year, Number(dotted[2]) - 1, Number(dotted[1]));
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function toDateKey(date) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayKey() {
  return toDateKey(new Date());
}

function getMondayOfWeek(date = new Date()) {
  const dayOfWeek = date.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff);
}

function isInThisWeek(cellDate, now = new Date()) {
  const monday = getMondayOfWeek(now);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  return cellDate >= monday && cellDate <= sunday;
}

function isInThisMonth(cellDate, now = new Date()) {
  return cellDate.getFullYear() === now.getFullYear() && cellDate.getMonth() === now.getMonth();
}

function isInThisYear(cellDate, now = new Date()) {
  return cellDate.getFullYear() === now.getFullYear();
}

function escapeRegExp(text) {
  return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function chipToRegExp(chip, { fromStart = false, toEnd = false } = {}) {
  const raw = String(chip || '').trim();
  if (!raw || !raw.replace(/\*/g, '').trim()) return null;
  const body = raw.split('*').map(escapeRegExp).join('.*');
  return new RegExp(`${fromStart ? '^' : ''}${body}${toEnd ? '$' : ''}`, 'i');
}

export function textMatchesWildcard(cell, pattern, { fromStart = false, toEnd = false } = {}) {
  if (isEmptyValue(cell) || pattern == null || String(pattern).trim() === '') return false;
  const raw = String(pattern).trim();
  if (!raw.includes('*')) {
    const cellText = toSearchString(cell);
    const needle = toSearchString(raw);
    if (fromStart && toEnd) return cellText === needle;
    if (fromStart) return cellText.startsWith(needle);
    if (toEnd) return cellText.endsWith(needle);
    return cellText.includes(needle);
  }
  const regex = chipToRegExp(raw, { fromStart, toEnd });
  return Boolean(regex && regex.test(String(cell)));
}

export function rowMatchesChip(row, chip, searchFields) {
  if (!String(chip || '').replace(/\*/g, '').trim()) return true;

  const fields = searchFields && searchFields.length > 0
    ? searchFields
    : Object.keys(row || {});

  return fields.some((field) => textMatchesWildcard(getFieldValue(row, field), chip));
}

export function rowMatchesCondition(row, condition) {
  const { column, operator, value } = condition || {};
  if (!column || !operator) return true;

  const cell = getFieldValue(row, column);

  switch (operator) {
    case 'isEmpty':
      return isEmptyValue(cell);
    case 'isNotEmpty':
      return !isEmptyValue(cell);
    case 'contains':
      return textMatchesWildcard(cell, value);
    case 'notContains':
      return isEmptyValue(cell) || !textMatchesWildcard(cell, value);
    case 'equals':
      return textMatchesWildcard(cell, value, { fromStart: true, toEnd: true });
    case 'notEquals':
      return isEmptyValue(cell) || !textMatchesWildcard(cell, value, { fromStart: true, toEnd: true });
    case 'startsWith':
      return textMatchesWildcard(cell, value, { fromStart: true });
    case 'endsWith':
      return textMatchesWildcard(cell, value, { toEnd: true });
    case 'isToday': {
      const cellDate = parseDate(cell);
      return Boolean(cellDate && toDateKey(cellDate) === getTodayKey());
    }
    case 'isThisWeek': {
      const cellDate = parseDate(cell);
      return Boolean(cellDate && isInThisWeek(cellDate));
    }
    case 'isThisMonth': {
      const cellDate = parseDate(cell);
      return Boolean(cellDate && isInThisMonth(cellDate));
    }
    case 'isThisYear': {
      const cellDate = parseDate(cell);
      return Boolean(cellDate && isInThisYear(cellDate));
    }
    case 'is': {
      const cellDate = parseDate(cell);
      const filterDate = parseDate(value);
      return Boolean(cellDate && filterDate && toDateKey(cellDate) === toDateKey(filterDate));
    }
    case 'before': {
      const cellDate = parseDate(cell);
      const filterDate = parseDate(value);
      return Boolean(cellDate && filterDate && cellDate < filterDate);
    }
    case 'after': {
      const cellDate = parseDate(cell);
      const filterDate = parseDate(value);
      return Boolean(cellDate && filterDate && cellDate > filterDate);
    }
    default:
      return true;
  }
}

export function applyTaskFilters(rows, { searchChips = [], conditions = [], searchFields } = {}) {
  if (!Array.isArray(rows)) return [];

  const chips = searchChips
    .map((chip) => String(chip || '').trim())
    .filter(Boolean);
  const validConditions = getValidConditions(conditions);

  return rows.filter((row) => {
    for (const chip of chips) {
      if (!rowMatchesChip(row, chip, searchFields)) return false;
    }
    for (const condition of validConditions) {
      if (!rowMatchesCondition(row, condition)) return false;
    }
    return true;
  });
}

export function extractSearchTerms(searchTextValue) {
  if (!searchTextValue) return [];
  const parts = String(searchTextValue).split(';').map((part) => part.trim()).filter(Boolean);
  const terms = [];
  for (const part of parts) {
    const matches = part.match(/[^*]+/g) || [];
    for (const match of matches) {
      const cleaned = match.trim();
      if (cleaned) terms.push(cleaned);
    }
  }
  return terms;
}

export function searchTextToChips(searchTextValue) {
  if (!searchTextValue) return [];
  return String(searchTextValue)
    .split(';')
    .map((part) => {
      let text = part.trim();
      if (text.startsWith('*') && text.endsWith('*') && text.length >= 2) {
        text = text.slice(1, -1);
      }
      return text.trim();
    })
    .filter((text) => text && text.replace(/\*/g, '').trim());
}

export function normalizeChipToWildcard(chip) {
  const raw = String(chip || '').trim();
  if (!raw || !raw.replace(/\*/g, '').trim()) return '';
  if (!raw.includes('*')) return `*${raw}*`;
  let next = raw;
  if (!next.startsWith('*')) next = `*${next}`;
  if (!next.endsWith('*')) next = `${next}*`;
  return next;
}

export function chipsToSearchText(chips) {
  return (Array.isArray(chips) ? chips : [])
    .map(normalizeChipToWildcard)
    .filter(Boolean)
    .join('; ');
}

export function formatConditionChipLabel(condition, t, getColumnLabel) {
  if (!condition) return '';
  const columnLabel = getColumnLabel
    ? getColumnLabel(condition.column)
    : t(`taskColumns.${condition.column}`, condition.column);
  const operatorLabel = t(`taskFilters.operators.${condition.operator}`, condition.operator);
  if (!operatorNeedsValue(condition.operator)) {
    return `${columnLabel} ${operatorLabel}`;
  }
  let valueLabel = condition.value ?? '';
  if (condition.column === 'report_status' && valueLabel) {
    valueLabel = t(`reportStatus.${valueLabel}`, valueLabel);
  }
  return `${columnLabel} ${operatorLabel} ${valueLabel}`.trim();
}

export function normalizeConditions(conditions) {
  return (Array.isArray(conditions) ? conditions : []).map((condition) => ({
    id: condition.id || createEmptyCondition().id,
    column: condition.column || '',
    operator: condition.operator || (isDateColumn(condition.column) ? 'is' : 'contains'),
    value: condition.value ?? ''
  }));
}

export function savedFilterHasConditions(filter) {
  return Boolean(filter && Array.isArray(filter.conditions));
}

export function legacyFilterToConditions(filter) {
  if (!filter) return [];
  const conditions = [];
  if (filter.modality) {
    conditions.push({
      id: 'legacy-modality',
      column: 'modality',
      operator: 'equals',
      value: filter.modality
    });
  }
  if (filter.status) {
    conditions.push({
      id: 'legacy-status',
      column: 'report_status',
      operator: 'equals',
      value: filter.status
    });
  }
  if (filter.isDynamicToday || filter.studyDate === 'TODAY') {
    conditions.push({
      id: 'legacy-studyDate',
      column: 'studyDate',
      operator: 'isToday',
      value: ''
    });
  } else if (filter.studyDate) {
    conditions.push({
      id: 'legacy-studyDate',
      column: 'studyDate',
      operator: 'is',
      value: filter.studyDate
    });
  }
  return conditions;
}

export function conditionsFromSavedFilter(filter) {
  if (savedFilterHasConditions(filter)) {
    return normalizeConditions(filter.conditions);
  }
  return normalizeConditions(legacyFilterToConditions(filter));
}
